import React, { useState, useMemo, useCallback } from 'react';
import ProfilePictureUpload from './ProfilePictureUpload';
import AvatarImage from './AvatarImage';
import { useUserAvatar } from '../utils/profilePictureUtils';

interface ProfileAvatarProps {
    className?: string;
    size?: 'small' | 'medium' | 'large';
    alt?: string;
    allowUpload?: boolean;
    userId?: number; // If provided, shows this user's avatar instead of current user's
    previewUrl?: string; // NEW: If provided, shows this preview instead of fetched avatar
    displayName?: string; // If provided, shows initials when no avatar is available
    imageUrl?: string; // S3 URL for athlete profile pictures from API
    skipAvatarFetch?: boolean; // If true, skips avatar fetching and only shows initials or imageUrl
}

// Helper function to get user initials from display name
const getInitials = (name: string): string => {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Separate component for current user avatar
const CurrentUserAvatar: React.FC<Omit<ProfileAvatarProps, 'userEmail'>> = React.memo(({
    className = '',
    size = 'medium',
    alt = 'Profile picture',
    allowUpload = false,
    previewUrl,
    displayName,
    imageUrl,
    skipAvatarFetch
}) => {
    const [uploadError, setUploadError] = useState<string>('');
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [imageError, setImageError] = useState(false);

    const isProfileFormContext = className.includes('pf-avatar-img') || className.includes('athlete-avatar-img') || className.includes('bb-avatar-img') || className.includes('tm-avatar') || className.includes('tm-profile-avatar');

    // For brand banner avatars, we need to maintain exact dimensions
    const isBrandBannerContext = className.includes('bb-avatar-img');

    // For team member avatars, we should not apply width/height 100% styles
    const isTeamMemberContext = className.includes('tm-avatar') || className.includes('tm-profile-avatar');

    const avatarClassName = useMemo(() => {
        if (isProfileFormContext) {
            return className;
        }
        const sizeClasses = {
            small: 'w-8 h-8',
            medium: 'w-12 h-12',
            large: 'w-24 h-24'
        };
        return `${className} ${sizeClasses[size]} rounded-full object-cover`;
    }, [className, size, isProfileFormContext]);

    const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        setImageError(true);
    }, []);

    const handleUploadSuccess = useCallback(() => {
        setUploadError('');
        setUploadSuccess(true);
        // Dispatch event to notify all components that profile picture has been updated
        window.dispatchEvent(new CustomEvent('profilePictureUpdated'));
        setTimeout(() => setUploadSuccess(false), 3000);
    }, []);

    // Reset imageError when imageUrl changes (new image URL from parent)
    React.useEffect(() => {
        setImageError(false);
    }, [imageUrl]);

    const handleUploadError = useCallback((error: string) => {
        setUploadError(error);
        setUploadSuccess(false);
        setTimeout(() => setUploadError(''), 5000);
    }, []);

    const imageSrc = useMemo(() => {
        // If there was an error loading the imageUrl, don't use it
        if (imageError && imageUrl) return undefined;
        // Priority: previewUrl > imageUrl (S3 URL from API)
        if (previewUrl) return previewUrl;
        if (imageUrl) return imageUrl;
        // Return undefined when no image available
        return undefined;
    }, [previewUrl, imageUrl, imageError]);

    // Show initials if we have displayName and no image available (or if image failed to load)
    const initials = displayName ? getInitials(displayName) : "";
    const hasImage = !!(previewUrl || (imageUrl && !imageError));
    const showInitials = !!initials && !hasImage;

    // Determine the correct CSS class suffix based on context
    const getInitialsClass = () => {
        if (className.includes('tm-profile-avatar')) return 'tm-avatar--initials';
        if (className.includes('tm-avatar')) return 'tm-avatar--initials';
        if (className.includes('bb-avatar-img')) return 'bb-avatar--initials';
        if (className.includes('pf-avatar-img')) return 'pf-avatar--initials';
        if (className.includes('athlete-avatar-img')) return 'athlete-avatar--initials';
        return 'bb-avatar--initials';
    };

    if (isProfileFormContext) {
        if (showInitials) {
            return (
                <div className={`${avatarClassName} ${getInitialsClass()}`}>
                    {initials}
                </div>
            );
        }
        // Only render image if we have an imageSrc
        if (imageSrc) {
            return (
                <AvatarImage
                    src={imageSrc}
                    alt={alt}
                    className={avatarClassName}
                    onError={handleImageError}
                    style={{
                        display: 'block',
                        width: (isBrandBannerContext || isTeamMemberContext) ? undefined : '100%',
                        height: (isBrandBannerContext || isTeamMemberContext) ? undefined : '100%',
                        objectFit: 'cover',
                        objectPosition: 'center'
                    }}
                />
            );
        }
        // Fallback: show placeholder crosshatch pattern
        return (
            <div className={avatarClassName} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f3f4f6'
            }}>
                <svg width="100%" height="100%" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <clipPath id="circleClip">
                            <circle cx="40" cy="40" r="40"/>
                        </clipPath>
                    </defs>
                    <g clipPath="url(#circleClip)">
                        <rect x="0" y="0" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="16" y="0" width="16" height="16" fill="#d1d5db"/>
                        <rect x="32" y="0" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="48" y="0" width="16" height="16" fill="#d1d5db"/>
                        <rect x="64" y="0" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="0" y="16" width="16" height="16" fill="#d1d5db"/>
                        <rect x="16" y="16" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="32" y="16" width="16" height="16" fill="#d1d5db"/>
                        <rect x="48" y="16" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="64" y="16" width="16" height="16" fill="#d1d5db"/>
                        <rect x="0" y="32" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="16" y="32" width="16" height="16" fill="#d1d5db"/>
                        <rect x="32" y="32" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="48" y="32" width="16" height="16" fill="#d1d5db"/>
                        <rect x="64" y="32" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="0" y="48" width="16" height="16" fill="#d1d5db"/>
                        <rect x="16" y="48" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="32" y="48" width="16" height="16" fill="#d1d5db"/>
                        <rect x="48" y="48" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="64" y="48" width="16" height="16" fill="#d1d5db"/>
                        <rect x="0" y="64" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="16" y="64" width="16" height="16" fill="#d1d5db"/>
                        <rect x="32" y="64" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="48" y="64" width="16" height="16" fill="#d1d5db"/>
                        <rect x="64" y="64" width="16" height="16" fill="#e5e7eb"/>
                    </g>
                </svg>
            </div>
        );
    }

    return (
        <div className="profile-avatar-container">
            {showInitials ? (
                <div className={`${avatarClassName} ${getInitialsClass()}`}>
                    {initials}
                </div>
            ) : imageSrc ? (
                <AvatarImage
                    src={imageSrc}
                    alt={alt}
                    className={avatarClassName}
                    onError={handleImageError}
                />
            ) : null}

            {allowUpload && (
                <div className="upload-section mt-2">
                    <ProfilePictureUpload
                        onUploadSuccess={handleUploadSuccess}
                        onUploadError={handleUploadError}
                        className="upload-button"
                    />

                    {uploadError && (
                        <div className="text-red-500 text-sm mt-1">{uploadError}</div>
                    )}

                    {uploadSuccess && (
                        <div className="text-green-500 text-sm mt-1">Picture uploaded successfully!</div>
                    )}
                </div>
            )}
        </div>
    );
}, (prevProps, nextProps) => {
    // Custom comparison function for React.memo
    return (
        prevProps.className === nextProps.className &&
        prevProps.size === nextProps.size &&
        prevProps.alt === nextProps.alt &&
        prevProps.allowUpload === nextProps.allowUpload &&
        prevProps.previewUrl === nextProps.previewUrl &&
        prevProps.displayName === nextProps.displayName &&
        prevProps.imageUrl === nextProps.imageUrl
    );
});

CurrentUserAvatar.displayName = 'CurrentUserAvatar';

// Separate component for other user avatar
const OtherUserAvatar: React.FC<Required<Pick<ProfileAvatarProps, 'userId'>> & Omit<ProfileAvatarProps, 'userId' | 'allowUpload'>> = React.memo(({
    className = '',
    size = 'medium',
    alt = 'Profile picture',
    userId,
    previewUrl,
    displayName,
    imageUrl
}) => {
    const [imageError, setImageError] = useState(false);
    const hasPresignedUrl = !!(imageUrl && imageUrl.trim());
    const { avatarUrl: fetchedAvatarUrl } = useUserAvatar(hasPresignedUrl ? undefined : userId);

    const isProfileFormContext = className.includes('pf-avatar-img') || className.includes('athlete-avatar-img') || className.includes('bb-avatar-img') || className.includes('tm-avatar') || className.includes('tm-profile-avatar');

    // For brand banner avatars, we need to maintain exact dimensions
    const isBrandBannerContext = className.includes('bb-avatar-img');

    // For team member avatars, we should not apply width/height 100% styles
    const isTeamMemberContext = className.includes('tm-avatar') || className.includes('tm-profile-avatar');

    const avatarClassName = useMemo(() => {
        if (isProfileFormContext) {
            return className;
        }
        const sizeClasses = {
            small: 'w-8 h-8',
            medium: 'w-12 h-12',
            large: 'w-24 h-24'
        };
        return `${className} ${sizeClasses[size]} rounded-full object-cover`;
    }, [className, size, isProfileFormContext]);

    const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        setImageError(true);
    }, []);

    React.useEffect(() => {
        setImageError(false);
    }, [fetchedAvatarUrl, imageUrl, previewUrl]);

    const imageSrc = useMemo(() => {
        if (imageError && (fetchedAvatarUrl || imageUrl)) return undefined;
        if (previewUrl) return previewUrl;
        if (hasPresignedUrl) return imageUrl;
        if (fetchedAvatarUrl) return fetchedAvatarUrl;
        if (imageUrl) return imageUrl;
        return undefined;
    }, [previewUrl, fetchedAvatarUrl, imageUrl, imageError, hasPresignedUrl]);

    // Show initials if we have displayName and no image available (or if image failed to load)
    const initials = displayName ? getInitials(displayName) : "";
    const hasImage = !!(previewUrl || (hasPresignedUrl && imageUrl && !imageError) || fetchedAvatarUrl || (imageUrl && !imageError));
    const showInitials = !!initials && !hasImage;

    // Determine the correct CSS class suffix based on context
    const getInitialsClass = () => {
        if (className.includes('tm-profile-avatar')) return 'tm-avatar--initials';
        if (className.includes('tm-avatar')) return 'tm-avatar--initials';
        if (className.includes('bb-avatar-img')) return 'bb-avatar--initials';
        if (className.includes('pf-avatar-img')) return 'pf-avatar--initials';
        if (className.includes('athlete-avatar-img')) return 'athlete-avatar--initials';
        return 'bb-avatar--initials';
    };


    if (isProfileFormContext) {
        if (showInitials) {
            return (
                <div className={`${avatarClassName} ${getInitialsClass()}`}>
                    {initials}
                </div>
            );
        }
        // Only render image if we have an imageSrc
        if (imageSrc) {
            return (
                <AvatarImage
                    src={imageSrc}
                    alt={alt}
                    className={avatarClassName}
                    onError={handleImageError}
                    style={{
                        display: 'block',
                        width: (isBrandBannerContext || isTeamMemberContext) ? undefined : '100%',
                        height: (isBrandBannerContext || isTeamMemberContext) ? undefined : '100%',
                        objectFit: 'cover',
                        objectPosition: 'center'
                    }}
                />
            );
        }
        // Fallback: show placeholder crosshatch pattern
        return (
            <div className={avatarClassName} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f3f4f6'
            }}>
                <svg width="100%" height="100%" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <clipPath id="circleClipOther">
                            <circle cx="40" cy="40" r="40"/>
                        </clipPath>
                    </defs>
                    <g clipPath="url(#circleClipOther)">
                        <rect x="0" y="0" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="16" y="0" width="16" height="16" fill="#d1d5db"/>
                        <rect x="32" y="0" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="48" y="0" width="16" height="16" fill="#d1d5db"/>
                        <rect x="64" y="0" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="0" y="16" width="16" height="16" fill="#d1d5db"/>
                        <rect x="16" y="16" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="32" y="16" width="16" height="16" fill="#d1d5db"/>
                        <rect x="48" y="16" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="64" y="16" width="16" height="16" fill="#d1d5db"/>
                        <rect x="0" y="32" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="16" y="32" width="16" height="16" fill="#d1d5db"/>
                        <rect x="32" y="32" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="48" y="32" width="16" height="16" fill="#d1d5db"/>
                        <rect x="64" y="32" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="0" y="48" width="16" height="16" fill="#d1d5db"/>
                        <rect x="16" y="48" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="32" y="48" width="16" height="16" fill="#d1d5db"/>
                        <rect x="48" y="48" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="64" y="48" width="16" height="16" fill="#d1d5db"/>
                        <rect x="0" y="64" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="16" y="64" width="16" height="16" fill="#d1d5db"/>
                        <rect x="32" y="64" width="16" height="16" fill="#e5e7eb"/>
                        <rect x="48" y="64" width="16" height="16" fill="#d1d5db"/>
                        <rect x="64" y="64" width="16" height="16" fill="#e5e7eb"/>
                    </g>
                </svg>
            </div>
        );
    }

    return (
        <div className="profile-avatar-container">
            {showInitials ? (
                <div className={`${avatarClassName} ${getInitialsClass()}`}>
                    {initials}
                </div>
            ) : imageSrc ? (
                <AvatarImage
                    src={imageSrc}
                    alt={alt}
                    className={avatarClassName}
                    onError={handleImageError}
                />
            ) : null}
        </div>
    );
});

OtherUserAvatar.displayName = 'OtherUserAvatar';

const PresignedOnlyAvatar: React.FC<Omit<ProfileAvatarProps, 'userId' | 'allowUpload' | 'skipAvatarFetch'>> = ({
    className = '',
    size = 'medium',
    alt = 'Profile picture',
    previewUrl,
    displayName,
    imageUrl,
}) => {
    const initials = displayName ? getInitials(displayName) : "";
    const isProfileFormContext = className.includes('pf-avatar-img') || className.includes('athlete-avatar-img') || className.includes('bb-avatar-img') || className.includes('tm-avatar') || className.includes('tm-profile-avatar');

    const avatarClassName = isProfileFormContext ? className : (() => {
        const sizeClasses = {
            small: 'w-8 h-8',
            medium: 'w-12 h-12',
            large: 'w-24 h-24'
        };
        return `${className} ${sizeClasses[size]} rounded-full object-cover`;
    })();

    const getInitialsClass = () => {
        if (className.includes('tm-profile-avatar')) return 'tm-avatar--initials';
        if (className.includes('tm-avatar')) return 'tm-avatar--initials';
        if (className.includes('bb-avatar-img')) return 'bb-avatar--initials';
        if (className.includes('pf-avatar-img')) return 'pf-avatar--initials';
        if (className.includes('athlete-avatar-img')) return 'athlete-avatar--initials';
        return 'bb-avatar--initials';
    };

    const rawSrc = (previewUrl && previewUrl.trim()) || (imageUrl && imageUrl.trim()) || null;

    if (rawSrc) {
        return (
            <AvatarImage
                src={rawSrc}
                alt={alt}
                className={avatarClassName}
                style={{
                    display: 'block',
                    objectFit: 'cover',
                    objectPosition: 'center'
                }}
            />
        );
    }

    if (initials) {
        return (
            <div className={`${avatarClassName} ${getInitialsClass()}`}>
                {initials}
            </div>
        );
    }

    return <div className={avatarClassName} />;
};

PresignedOnlyAvatar.displayName = 'PresignedOnlyAvatar';

// Main component that decides which sub-component to render
const ProfileAvatar: React.FC<ProfileAvatarProps> = React.memo(({
    userId,
    displayName,
    imageUrl,
    previewUrl,
    className = '',
    size = 'medium',
    alt = 'Profile picture',
    allowUpload = false,
    skipAvatarFetch = false,
}) => {
    // If skipAvatarFetch is true, use presigned/preview URLs only — no backend blob fetch
    if (skipAvatarFetch) {
        return (
            <PresignedOnlyAvatar
                displayName={displayName}
                imageUrl={imageUrl}
                previewUrl={previewUrl}
                className={className}
                size={size}
                alt={alt}
            />
        );
    }

    // If userId is provided, use OtherUserAvatar, otherwise use CurrentUserAvatar
    if (userId) {
        return <OtherUserAvatar key={`user-${userId}`} userId={userId} displayName={displayName} imageUrl={imageUrl} previewUrl={previewUrl} className={className} size={size} alt={alt} />;
    }

    return <CurrentUserAvatar key="current-user" displayName={displayName} imageUrl={imageUrl} previewUrl={previewUrl} className={className} size={size} alt={alt} allowUpload={allowUpload} skipAvatarFetch={skipAvatarFetch} />;
}, (prevProps, nextProps) => {
    // Custom comparison function for React.memo to prevent unnecessary re-renders
    return (
        prevProps.userId === nextProps.userId &&
        prevProps.displayName === nextProps.displayName &&
        prevProps.imageUrl === nextProps.imageUrl &&
        prevProps.previewUrl === nextProps.previewUrl &&
        prevProps.className === nextProps.className &&
        prevProps.size === nextProps.size &&
        prevProps.alt === nextProps.alt &&
        prevProps.allowUpload === nextProps.allowUpload &&
        prevProps.skipAvatarFetch === nextProps.skipAvatarFetch
    );
});

ProfileAvatar.displayName = 'ProfileAvatar';

export default ProfileAvatar;
